import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

export interface Profile {
  customerNumber?: string;
  name?: string;
  company?: string;
  userType?: string;
  userName?: string;
  id?: number;
  city?: string;
  state?: string;
  address?: string;
  address2?: string;
  zip?: string;
  country?: string;
  countryCode?: string;
}

interface ProfileListProps {
  profiles: Profile[];
  onSelect: (profile: Profile) => void;
  currentCustomerNumber?: string;
  variant?: 'light' | 'dark';
}

export const ProfileList = ({ profiles, onSelect, currentCustomerNumber, variant = 'light' }: ProfileListProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const isDark = variant === 'dark';

  const switchable = profiles.filter(p => p.userType === 'Profile' && p.customerNumber !== currentCustomerNumber);

  if (switchable.length === 0) {
    return null;
  }

  const filtered = switchable.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return [p.company, p.name, p.customerNumber, p.city, p.state]
      .some(field => field?.toLowerCase().includes(term));
  });

  const renderProfileButton = (profile: Profile, index: number) => {
    const displayName = profile.company || profile.name || `Profile ${profile.customerNumber || '(unknown)'}`;
    const city = profile.city || '';
    const state = profile.state || '';
    const location = [city, state].filter(Boolean).join(', ');
    const isMissing = !profile.customerNumber;

    const buttonContent = (
      <button
        key={profile.customerNumber || index}
        onClick={isMissing ? undefined : () => onSelect(profile)}
        disabled={isMissing}
        className={`w-full py-2 px-4 rounded-lg border flex items-center justify-between gap-3 transition-colors ${
          isMissing ? 'opacity-50 cursor-not-allowed' : ''
        } ${
          isDark
            ? 'border-white/20 bg-white/10 hover:bg-white/20 text-white'
            : 'border-border bg-card hover:bg-accent text-foreground'
        } ${isMissing && !isDark ? 'hover:bg-card' : ''} ${isMissing && isDark ? 'hover:bg-white/10' : ''}`}
      >
        <div className="flex-1 min-w-0 text-left">
          <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-foreground'}`}>
            {displayName} {profile.customerNumber ? `#${profile.customerNumber}` : ''}
          </div>
          {location && (
            <div className={`text-xs ${isDark ? 'text-white/50' : 'text-muted-foreground'}`}>
              {location}
            </div>
          )}
        </div>
        <span className={`text-xs font-medium shrink-0 ${isDark ? 'text-white/60' : 'text-muted-foreground'}`}>
          {isMissing ? '' : 'Select'}
        </span>
      </button>
    );

    if (isMissing) {
      return (
        <TooltipProvider key={profile.id || index}>
          <Tooltip>
            <TooltipTrigger asChild>
              {buttonContent}
            </TooltipTrigger>
            <TooltipContent>Missing Customer Number</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return buttonContent;
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-white/50' : 'text-muted-foreground'}`} />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, number, city or state..."
          className={`pl-10 ${isDark ? 'bg-transparent text-white border-white/20 placeholder:text-white/40 focus:border-white/40' : ''}`}
        />
      </div>
      <div className={`max-h-[300px] overflow-y-auto space-y-2 pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full ${isDark ? '[&::-webkit-scrollbar-track]:bg-white/5 [&::-webkit-scrollbar-thumb]:bg-white/20' : '[&::-webkit-scrollbar-thumb]:bg-muted-foreground/20'}`}>
        {filtered.length === 0 ? (
          <p className={`text-center py-4 text-sm ${isDark ? 'text-white/50' : 'text-muted-foreground'}`}>
            No profiles match your search.
          </p>
        ) : (
          filtered.map((profile, index) => renderProfileButton(profile, index))
        )}
      </div>
    </div>
  );
};
